import { createApiRef } from '@backstage/core-plugin-api';

/**
 * Why IMC declined to start a run. Closed vocabulary, mirrored from IMC's
 * `AnalysisWire.Reason` — see `docs/features/0040-analyze-now-per-system.md`.
 */
export type AnalysisRefusalReason =
  | 'fleet-collection-running'
  | 'another-system-running'
  | 'cooldown';

/** Where a System's analysis stands. Mirrors IMC's `AnalysisWire.State`. */
export type AnalysisState = 'idle' | 'running' | 'completed' | 'failed';

export type AnalysisStatus = {
  state: AnalysisState;
  runId?: string;
  completedMetrics: number;
  totalMetrics: number;
  currentMetric?: string;
  startedAt?: string;
  completedAt?: string;
  cooldownUntil?: string;
};

export type AnalysisStarted = {
  runId: string;
  systemName: string;
  totalMetrics: number;
  startedAt: string;
};

export type AnalysisRefused = {
  reason: AnalysisRefusalReason;
  retryAfterSeconds?: number;
  blockingSystemName?: string;
};

/**
 * The outcome of pressing the button: IMC either admitted the run or refused it, and a refusal is
 * an ordinary answer rather than an error — it is the case the strip exists to explain.
 */
export type AnalysisStartOutcome =
  | { admitted: true; run: AnalysisStarted }
  | { admitted: false; refused: AnalysisRefused };

/**
 * Asks IMC to re-collect one System's Engineering metrics now, instead of waiting for the weekly
 * run, and reports how that is going (feature 0040, ADR-0024).
 *
 * Unconfigured on purpose in local development: `scorecards.analysisUrl` has no default, and the
 * UI hides the control when it is absent, so the standard local portal check keeps working against
 * the static scorecard mock — which cannot answer a POST.
 */
export const analysisApiRef = createApiRef<AnalysisApi>({
  id: 'plugin.score-card.analysis.service',
});

export type AnalysisApi = {
  /** Whether the feature is configured at all. False ⇒ the caller renders nothing. */
  isEnabled(): boolean;
  start(systemName: string): Promise<AnalysisStartOutcome>;
  getStatus(systemName: string): Promise<AnalysisStatus | undefined>;
};
